import { AuthoredAttachments } from './AuthoredAttachments';
// HeroPostCard.tsx — expanded post card for the post-detail page.
// Reuses the feed sub-pieces (AuthorLink/PostBody/PostMedia/PostStatus/PostMoreMenu/ReportModal)
// and adds a timestamp meta line, a stats summary row, and the Bookmark action.
// Unlike PostCard, it is NOT a clickable navigation target.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel, MetaRow } from '@/ui';
import type { Post } from '@/core/models/post';
import { usePostStatus, useRequireAuth, usePreferences } from '@/hooks';
import { formatAbsoluteParts } from '@/core/lib/formatPostTime';
import { Icon } from '@/icons';
import { ReportModal } from '@/components/ReportModal';
import { AppealModal } from '@/components/AppealModal';
import { formatCount } from '@/components/formatCount';
import { AuthorLink } from './AuthorLink';
import { Coauthors } from './Coauthors';
import { PostBody } from './PostBody';
import { PostMedia } from './PostMedia';
import { PostStatus } from './PostStatus';
import { PostActions } from './PostActions';
import { DiscussionBell } from '@/features/discussions/DiscussionBell';
import { PostMoreMenu } from './PostMoreMenu';
import { EditPostDialog } from './EditPostDialog';
import { DeletePostDialog } from './DeletePostDialog';
import { DeletedTombstone } from './DeletedTombstone';
import { QuoteEmbed } from './QuoteEmbed';
import { ClipEmbed } from './ClipEmbed';
import { LinkPreviewCard, linkPreviewToShow } from './LinkPreviewCard';
import { ThreadChip } from './ThreadChip';
import { useOwnPostActions } from './useOwnPostActions';
import { PostPoll } from './PostPoll';
import { AudienceBadge, ScheduledLabel, DraftLabel } from './AudienceBadge';

export interface HeroPostCardProps {
  post: Post;
  /** Called when the Comment action is pressed (e.g. focus the reply composer). */
  onComment?: () => void;
}

const Article = styled(ConsolePanel).attrs({ as: 'article', $surfaceRole: 'reading' })`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 8px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const AuthorSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroBody = styled(PostBody)`
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: 1.5;
`;

const MetaLine = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  row-gap: 8px;
  column-gap: 0.9em;
  flex-wrap: wrap;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};

  font-variant-numeric: tabular-nums;
  b {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
  }
`;

const OwnerRemovedTombstone = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  background: ${({ theme }) => `${theme.color.danger}24`};
  color: ${({ theme }) => theme.color.danger};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const TombstoneText = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

// Fallback for posts with no createdAt (the two decorative seed variants) — a
// representative string rather than a blank meta line.
const TIMESTAMP_FALLBACK = ['3:30 PM', 'May 14, 2024'];

export function HeroPostCard({ post, onComment }: HeroPostCardProps) {
  const status = usePostStatus(post.id);
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const { prefs } = usePreferences();
  const [reporting, setReporting] = useState(false);
  const [appealingCaseId, setAppealingCaseId] = useState<string | null>(null);
  const own = useOwnPostActions(post);

  // Real timestamp from createdAt (server sends it), honouring the clock-format
  // pref; the hero line always shows the absolute form — that's its job.
  const absoluteParts = post.createdAt
    ? formatAbsoluteParts(post.createdAt, prefs.clockFormat)
    : [];
  const heroTime = absoluteParts.length > 0 ? absoluteParts : TIMESTAMP_FALLBACK;

  const councilRemoved = status?.decision?.outcome === 'removed';
  const ownerRemoved = post.removed === true;
  const removed = councilRemoved || ownerRemoved;
  const authorDeleted = post.deleted === true;
  const scheduled = post.published === false;
  const disabledReason: 'deleted' | 'scheduled' | undefined = authorDeleted
    ? 'deleted'
    : scheduled
      ? 'scheduled'
      : undefined;
  const linkPreview = linkPreviewToShow(post);

  function onReport() {
    if (!requireAuth('report')) return;
    setReporting(true);
  }
  function onAppeal(caseId: string) {
    if (!requireAuth('appeal')) return;
    // The server requires a written reason — collect it before POSTing.
    setAppealingCaseId(caseId);
  }

  function onHide() {
    // PostMoreMenu already toasts "You won't see this post again" (with Undo) — this
    // only needs to navigate away. Unlike the feed (where the card just disappears in
    // place), the detail page IS this post, so staying here would read as a no-op.
    navigate('/');
  }

  return (
    <Article aria-label={`Post by ${post.name}`} data-post-id={post.id}>
      <TopRow>
        <AuthorSlot>
          <AuthorLink post={post} size={54} />
          <Coauthors post={post} />
        </AuthorSlot>
        {!post.deleted && !post.removed && <DiscussionBell postId={post.id} />}
        <PostMoreMenu
          post={post}
          isOwn={own.isOwn}
          onReport={onReport}
          onEdit={own.openEdit}
          onDelete={own.openDelete}
          onHide={onHide}
        />
      </TopRow>

      {/* Deleted-by-author tombstone — body stays visible, interactions go dead */}
      {authorDeleted && <DeletedTombstone size="lg" />}

      {(scheduled || (post.audience && post.audience.type !== 'everyone')) && (
        <BadgeRow>
          {scheduled && post.scheduledAt && <ScheduledLabel scheduledAt={post.scheduledAt} />}
          {scheduled && !post.scheduledAt && <DraftLabel />}
          {post.audience && <AudienceBadge audience={post.audience} />}
        </BadgeRow>
      )}

      {!removed && (
        <>
          <HeroBody body={post.body} />
          <AuthoredAttachments
            attachments={post.attachments}
            perspectives={post.perspectives ?? undefined}
          />
          {post.poll ? (
            <PostPoll postId={post.id} poll={post.poll} />
          ) : (
            <PostMedia post={post} media={post.media} variant="hero" priority />
          )}
          {post.quotedPost && <QuoteEmbed quotedPost={post.quotedPost} />}
          {post.clip && <ClipEmbed clip={post.clip} postId={post.id} />}
          {/* 1.24.00 C1: at most one card under the body — see linkPreviewToShow. */}
          {linkPreview && <LinkPreviewCard preview={linkPreview} />}
          {/* 1.24.00 C2: a part links back to the thread root from its own page. */}
          {post.thread && <ThreadChip thread={post.thread} />}
          <MetaLine>
            <MetaRow>
              {heroTime.map((part) => (
                <span key={part}>{part}</span>
              ))}
              <span>TruXSocial for Web</span>
            </MetaRow>
          </MetaLine>
          <StatsRow>
            <span>
              <b>{formatCount(post.cheers)}</b> Cherishes
            </span>
            <span>
              <b>{formatCount(post.spreads)}</b> Spreads
            </span>
            <span>
              <b>{formatCount(post.quotes ?? 0)}</b> Quotes
            </span>
          </StatsRow>
        </>
      )}

      {ownerRemoved && (
        <OwnerRemovedTombstone aria-label="Removed by moderator">
          <Icon name="ban" size={18} />
          <TombstoneText>
            Removed by moderator
            {post.removedReason ? `: ${post.removedReason}` : ''}
          </TombstoneText>
        </OwnerRemovedTombstone>
      )}

      <PostActions
        post={post}
        size="lg"
        showBookmark
        onComment={onComment}
        disabledReason={disabledReason}
      />

      <PostStatus status={status} onAppeal={onAppeal} />

      {reporting && (
        <ReportModal target={{ kind: 'post', post }} onClose={() => setReporting(false)} />
      )}

      {/* Appeal modal — collects the required reason */}
      {appealingCaseId && (
        <AppealModal caseId={appealingCaseId} onClose={() => setAppealingCaseId(null)} />
      )}

      {/* Edit dialog — own post only */}
      {own.editing && (
        <EditPostDialog
          post={post}
          onClose={own.closeEdit}
          onSave={own.saveEdit}
          editWindowAdvisory={own.editWindowAdvisory}
        />
      )}

      {/* Delete confirm — own post only */}
      <DeletePostDialog
        open={own.deleting}
        onCancel={own.closeDelete}
        onConfirm={() => void own.confirmDelete()}
      />
    </Article>
  );
}
